import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Layout, Breadcrumb, message, Spin } from 'antd';
import { 
  HomeOutlined, 
  ProjectOutlined, 
  ShareAltOutlined
} from '@ant-design/icons';
import AgentListView from '../components/StateGraph/AgentListView';
import StateGraphEditorModal from '../components/StateGraph/StateGraphEditorModal';

const { Content } = Layout;

const StateGraphPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState<any>(null);
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  useEffect(() => {
    // Load project details
    const loadProject = async () => {
      try {
        if (projectId && projectId !== ':projectId') {
          // Validate that projectId is not a placeholder
          if (projectId.startsWith(':') || projectId.includes(':')) {
            throw new Error('Invalid project ID');
          }
          
          // Simulate API call to get project details
          // const response = await fetch(`/api/v1/projects/${projectId}`);
          // const projectData = await response.json();
          
          // Mock project data for now
          setProject({
            id: projectId,
            name: 'AI RAG Project',
            description: 'Intelligent document retrieval and response generation'
          });
        } else {
          throw new Error('No valid project ID provided');
        }
      } catch (error) {
        console.error('Project loading error:', error);
        message.error('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [projectId]);

  // Handler for editing an agent - opens full-screen editor
  const handleEditAgent = (agentId: string) => {
    setSelectedAgentId(agentId);
    setShowEditorModal(true);
  };

  // Handler for creating a new agent - opens full-screen editor
  const handleCreateAgent = () => {
    setSelectedAgentId(null);
    setShowEditorModal(true);
  };

  // Handler for closing the editor modal
  const handleCloseEditor = () => {
    setShowEditorModal(false);
    setSelectedAgentId(null);
  };

  if (loading) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Content style={{ padding: '0' }}>
        {/* Breadcrumb */}
        <div style={{ 
          padding: '16px 24px', 
          background: '#fff', 
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Breadcrumb
            items={[
              {
                href: '/',
                title: <HomeOutlined />,
              },
              {
                href: '/projects',
                title: (
                  <>
                    <ProjectOutlined />
                    <span>Projects</span>
                  </>
                ),
              },
              {
                href: `/projects/${projectId}`,
                title: project?.name || 'Project',
              },
              {
                title: (
                  <>
                    <ShareAltOutlined />
                    <span>AI Agent Builder</span>
                  </>
                ),
              },
            ]}
          />
        </div>

        {/* Agent List View */}
        <div style={{ height: 'calc(100vh - 112px)', background: '#f5f5f5' }}>
          {projectId && !projectId.startsWith(':') ? (
            <AgentListView 
              projectId={projectId}
              onEditAgent={handleEditAgent}
              onCreateAgent={handleCreateAgent}
            />
          ) : (
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <p>Invalid project ID. Please check the URL and try again.</p>
            </div>
          )}
        </div>

        {/* Full-Screen Graph Editor Modal */}
        <StateGraphEditorModal
          visible={showEditorModal}
          onClose={handleCloseEditor}
          projectId={projectId || ''}
          agentId={selectedAgentId}
        />
      </Content>
    </Layout>
  );
};

export default StateGraphPage;